/**
 * Console links the browser cannot show, handed to the desktop app.
 *
 * `/app/...` links name a session citation or something in a Personal page,
 * which only the author's desktop holds. The page tries the `nimbalyst://console/...` deep link once on arrival
 * and keeps a button for it, so a browser that blocked the automatic attempt,
 * or a reader without the app, still has somewhere to go.
 *
 * A Claude Code citation has no such place: the session is on its author's
 * machine and nowhere else, so the page says so and offers nothing.
 */
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { DESKTOP_DOWNLOAD_URL } from '../auth/desktopHandoff';
import { Wordmark } from '../components/Chrome';
import { BUTTON_PRIMARY_LG, CALLBACK_CARD, CALLBACK_SCREEN } from '../controlStyles';
import { appLinkLanding, teamViewLanding, type AppLinkLanding } from './consoleLinkLanding';

const COPY: Record<Exclude<AppLinkLanding['kind'], 'claude-code-citation'>, { title: string; message: string }> = {
  citation: {
    title: 'This quote comes from a Nimbalyst session',
    message: 'Sessions stay on the computer they ran on, so the conversation opens in Nimbalyst there. The quote itself is in the page you came from.',
  },
  page: {
    title: 'This is a Personal page',
    message: 'Personal pages are kept on their author’s devices and open in Nimbalyst there.',
  },
  item: {
    title: 'This page is in someone’s Personal pages',
    message: 'Personal pages are kept on their author’s devices and open in Nimbalyst there.',
  },
  type: {
    title: 'This type is in someone’s Personal pages',
    message: 'Personal pages are kept on their author’s devices and open in Nimbalyst there.',
  },
  view: {
    title: 'This view is in someone’s Personal pages',
    message: 'Personal pages are kept on their author’s devices and open in Nimbalyst there.',
  },
};

function OpenInAppScreen({ deepLink, title, message, state }: { deepLink: string | null; title: string; message: string; state?: string }) {
  useEffect(() => {
    if (deepLink) window.location.href = deepLink;
  }, [deepLink]);
  return (
    <main className={CALLBACK_SCREEN}>
      <div className={`open-in-app ${CALLBACK_CARD} grid gap-5`} data-open-in-app={state ?? (deepLink ? 'offered' : 'invalid')}>
        <div className="justify-self-center"><Wordmark /></div>
        <div className="grid gap-2">
          <strong className="text-[1.05rem] text-nim">{title}</strong>
          <span>{message}</span>
        </div>
        {deepLink && (
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a className={BUTTON_PRIMARY_LG} href={deepLink}>Open in Nimbalyst</a>
            <a className="font-medium text-[var(--nim-primary)] no-underline hover:underline" href={DESKTOP_DOWNLOAD_URL}>
              Download Nimbalyst
            </a>
          </div>
        )}
      </div>
    </main>
  );
}

/** `/app/*`: no sign-in needed, since nothing here is read from the server. */
export function OpenInAppPage() {
  const { pathname, search } = useLocation();
  const landing = appLinkLanding(pathname, search);
  if (!landing) {
    return <OpenInAppScreen deepLink={null} title="This link is not valid" message="It may have been copied incompletely." />;
  }
  if (landing.kind === 'claude-code-citation') {
    return (
      <OpenInAppScreen
        deepLink={null}
        state="claude-code"
        title="This quote comes from a Claude Code session"
        message="That session is on the computer of the person who quoted it, and Nimbalyst keeps no copy of it. The quote itself is in the page you came from."
      />
    );
  }
  return <OpenInAppScreen deepLink={landing.deepLink} {...COPY[landing.kind]} />;
}

/** `/org/:org/project/:project/view/*`: a view placed in a page, shown on its Pages route. */
export function TeamViewLinkRoute() {
  const { pathname, search } = useLocation();
  const landing = teamViewLanding(pathname, search);
  if (!landing) {
    return <OpenInAppScreen deepLink={null} title="This link is not valid" message="It may have been copied incompletely." />;
  }
  return <Navigate to={landing.path} replace />;
}
